import React, { useEffect, useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler,
} from "chart.js";
import api from "../../api";
import PanelHeading from "./PanelHeading";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const money = (v) => `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PARTS = [
  { key: "registration", label: "Registrations" },
  { key: "installments", label: "Instalments" },
  { key: "mock_exams", label: "Mock exams" },
  { key: "past_papers", label: "Past papers" },
];

/**
 * Course-side revenue for the dashboard's date range: course registrations and instalments paid,
 * plus mock exam and past paper sales (free grants excluded). Mobile plan revenue is on the
 * App Users tab.
 */
export default function CourseRevenueCard({ params }) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});

  useEffect(() => {
    let cancelled = false;
    api.get("admin/dashboard/course-revenue", { params: JSON.parse(key) })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [key]);

  if (!data) {
    return (
      <Box>
        <PanelHeading title="Course Revenue" />
        <Typography variant="body2" color="text.secondary">Not available for your account.</Typography>
      </Box>
    );
  }

  const deltaPct = Number(data.delta_pct ?? 0);
  const up = deltaPct >= 0;

  return (
    <Box>
      <PanelHeading title="Course Revenue" hint="Paid course registrations and instalments, plus mock exam and past paper sales. Free grants are not counted." />
      <Typography variant="h4" fontWeight={800} sx={{ color: "#111827", lineHeight: 1.2 }}>
        {money(data.total)}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
        {up ? <TrendingUpIcon sx={{ fontSize: 14, color: "#16a34a" }} /> : <TrendingDownIcon sx={{ fontSize: 14, color: "#dc2626" }} />}
        <Typography variant="caption" sx={{ color: up ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
          {Math.abs(deltaPct)}% {data.delta_label || ""}
        </Typography>
      </Stack>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.5 }}>
        {PARTS.map((p) => (
          <Chip key={p.key} size="small" variant="outlined" label={`${p.label} ${money(data.breakdown?.[p.key])}`} />
        ))}
      </Box>
      <Box sx={{ height: 150, width: "100%" }}>
        <Line
          data={{
            labels: data.labels || [],
            datasets: [{
              data: data.series || [],
              borderColor: "#0d9488",
              backgroundColor: "rgba(13,148,136,0.12)",
              fill: true,
              tension: 0.4,
              pointRadius: 2,
            }],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (ctx) => money(ctx.parsed.y) } },
            },
            scales: {
              y: { beginAtZero: true, grid: { color: "#f1f5f9" }, ticks: { color: "#374151", callback: (v) => `£${v}` } },
              x: { grid: { display: false }, ticks: { color: "#374151", maxRotation: 0, autoSkip: true } },
            },
          }}
        />
      </Box>
    </Box>
  );
}
